/**
 * Customer Selector
 * Dropdown to switch between customers (admin only)
 */

import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';

import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setActiveCustomer } from '../../store/slices/customerSlice';
import { initAssets, fetchAssetsByCustomer } from '../../store/slices/assetSlice';
import { initProjects, fetchProjectsByCustomer } from '../../store/slices/projectSlice';
import { clearMap } from '../../store/slices/mapSlice';
import { colorPalette } from '../../theme';

const CustomerSelector = () => {
  const dispatch = useAppDispatch();
  const { customerList, activeCustomer } = useAppSelector((state) => state.customer);

  // R-11 (ASS-20) widened: exclude Inactive rather than include Active.
  // A whitelist on 'Active' silently drops customers whose status is missing,
  // null, or an unexpected string (operator confirmed 2026-09-09T18:40Z that
  // some Portica records have NO status field at all). A blacklist on
  // 'Inactive' shows every other customer while still hiding the disabled ones,
  // which matches the ticket's actual guard: disabled customers do not appear
  // in the switcher, but every other customer remains switchable.
  const inactiveStatuses = new Set(['Inactive', 'inactive']);
  const activeCustomers = customerList.filter((c) => !inactiveStatuses.has(c.status));

  const handleChange = async (event) => {
    const customerId = event.target.value;
    const customer = customerList.find((c) => c.id === customerId);
    
    if (customer) {
      // Clear existing data
      dispatch(initAssets());
      dispatch(initProjects());
      dispatch(clearMap());
      
      // Set new active customer
      dispatch(setActiveCustomer(customer));
      
      // Load new customer data
      await dispatch(fetchAssetsByCustomer({ customerId: customer.id }));
      await dispatch(fetchProjectsByCustomer(customer.id));
    }
  };

  if (activeCustomers.length <= 1) {
    return null;
  }

  return (
    <FormControl fullWidth size="small">
      <Select
        value={activeCustomer?.id || ''}
        displayEmpty
        onChange={handleChange}
        sx={{
          '& .MuiSelect-select': {
            color: colorPalette.portlandCement,
          },
        }}
      >
        {activeCustomers.map((customer) => (
          <MenuItem key={customer.id} value={customer.id}>
            <Typography noWrap>{customer.name}</Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CustomerSelector;
